import React, { useState, useEffect, useRef } from "react";
import './OutputScreen.css';
import { ReactComponent as HouseImage } from '../Assets/house_image.svg';
import { ReactComponent as QuietStreet } from '../Assets/quiet_street.svg';
import { ReactComponent as FineTuneImage } from '../Assets/fine_tune_image.svg';
import { ReactComponent as SeasonChangeImage } from '../Assets/season_change_image.svg';
import { ReactComponent as DataTrendsImage } from '../Assets/data_trends_image.svg';
import ContactUsImage from '../Assets/contact_us_image.svg';

import {
  calculateAnnualGeneration,
  calculateConsumptionExport,
  calculateYear1Savings,
  calculateLifetimeSavings,
  calculateInstallationCost,
  calculatePaybackPeriod,
  normalizeOrientation,
  calculateViabilityScore,
} from "../utils/SolarCalculations";
import { getNormalizedIrradiance } from "../data/irradianceZones";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

const ORIENTATION_NARRATIVE = {
  south:     'Your roof faces south, which is the optimal orientation for solar in the UK. South-facing panels receive the maximum possible direct sunlight throughout the day, producing significantly more energy than any other direction. This single factor has a large positive impact on your generation output and overall returns.',
  southeast: 'Your roof faces south-east, which is an excellent orientation for solar in the UK. South-east facing panels capture strong morning sun and maintain high output through the middle of the day. The performance difference compared to due south is small and this orientation is widely considered ideal for solar generation.',
  southwest: 'Your roof faces south-west, which is an excellent orientation for solar in the UK. South-west facing panels capture strong afternoon sun and perform very well through the middle of the day. The performance difference compared to due south is small and this orientation is widely considered ideal for solar generation.',
  east:      'Your roof faces east, which will generate good solar output during morning hours. East-facing panels perform well in the morning and around midday but see a significant drop in the afternoon. Total daily generation will be meaningfully lower than a south-facing system, which is reflected in your score. However, east-facing systems can still deliver solid returns, particularly if your household energy use is concentrated in the morning.',
  west:      'Your roof faces west, which will generate good solar output during afternoon hours. West-facing panels perform well from midday into the evening and can be a useful match for households with higher afternoon and evening demand. Total daily generation will be meaningfully lower than a south-facing system, which is reflected in your score, but a well-sized west-facing system can still deliver solid returns.',
  northeast: 'Your roof faces north-east, which is the most challenging orientation for solar in the UK. North-east facing panels receive limited direct sunlight and will generate significantly less energy than south, east or west-facing systems. While an installation may still be viable depending on your consumption and system size, the orientation is the main constraint on your viability score and it is important to get a thorough assessment before proceeding.',
  northwest: 'Your roof faces north-west, which is the most challenging orientation for solar in the UK. North-west facing panels receive limited direct sunlight and will generate significantly less energy than south, east or west-facing systems. While an installation may still be viable depending on your consumption and system size, the orientation is the main constraint on your viability score and it is important to get a thorough assessment before proceeding.',
  north:     'Your roof faces north, which is the most challenging orientation for solar in the UK. North-facing panels receive very little direct sunlight and will generate substantially less energy than any other direction. This significantly affects the financial case for solar at your property. It does not necessarily rule out an installation, but a thorough independent assessment is essential before any decision is made.',
};
const getOrientationNarrative = (o) =>
  ORIENTATION_NARRATIVE[normalizeOrientation(o || 'S')] ?? ORIENTATION_NARRATIVE.south;

const getShadingNarrative = (pct, time) => {
  const p = Number(pct) || 0;
  if (p === 0) {
    return 'You indicated that your roof is unaffected by shading. This is the ideal scenario for solar. Unobstructed panels generate the maximum possible amount of free electricity throughout the day, with no losses from shadows cast by trees, chimneys, neighbouring buildings or other obstacles. This factor makes a strong positive contribution to your viability score.';
  }
  if (p <= 25) {
    const timeMap = {
      Morning:   'Minor morning shading means your panels will be at full output for the majority of the solar day, from late morning through to the afternoon. The impact on total generation is small and unlikely to affect your financial returns significantly.',
      Afternoon: 'Minor afternoon shading means your panels will be at full output during the morning and the peak solar hours around midday. The impact on total generation is small and unlikely to affect your financial returns significantly.',
      'all-day': 'Light shading across the whole day has a modest cumulative effect on total generation. While some output is lost at various points, the overall impact is manageable and should not significantly undermine your financial returns.',
    };
    return timeMap[time] || timeMap['all-day'];
  }
  if (p <= 50) {
    const timeMap = {
      Morning:   'Moderate morning shading will reduce your panel output during the early part of the solar day. If your household demand is concentrated in the afternoon and evening you may find the impact on self-consumption is limited, but the overall generation loss is meaningful and is reflected in your score.',
      Afternoon: 'Moderate afternoon shading will reduce your panel output during a portion of the peak solar period. East-facing panels can sometimes mitigate this effect. The generation loss is meaningful and is reflected in your score, and we recommend a full site assessment to explore your options.',
      'all-day': 'Moderate shading throughout the day creates cumulative losses across the full generation period. This is a significant factor affecting both the output of your system and your overall viability score. A full shading analysis is strongly recommended before you commit to an installation.',
    };
    return timeMap[time] || timeMap['all-day'];
  }
  return 'Significant shading is present at your property and this is having a material impact on your viability score. Heavy shading reduces panel output substantially, which affects both the energy generated and the financial returns from an installation. We strongly recommend a professional shading analysis before proceeding. There may be options such as alternative panel positioning, microinverters or optimisers that can reduce the impact, but these need to be assessed properly with eyes on your property.';
};

const getLocationNarrative = (normalizedIrradiance) => {
  if (normalizedIrradiance >= 0.76) return 'Your property is located in one of the highest solar irradiance zones in the UK. Properties in the south of England and along the south coast benefit from significantly more direct sunlight hours than the national average, which translates directly into higher annual generation and stronger financial returns. Location is a meaningful positive factor in your viability score.';
  if (normalizedIrradiance >= 0.59) return 'Your property is located in a good solar irradiance zone. The south-east, London, East Anglia and parts of the Midlands typically enjoy above-average solar resource, giving your system a solid foundation for consistent generation across the year. This factor makes a positive contribution to your viability score.';
  if (normalizedIrradiance >= 0.41) return 'Your property is located in a moderate solar irradiance zone. The Midlands, Yorkshire and parts of Wales receive a reasonable solar resource, broadly in line with the UK average. Generation will be somewhat lower than in the south of England but the difference is manageable and a well-sized system can still deliver strong returns.';
  return 'Your property is located in a lower solar irradiance zone. Scotland, the far north of England and parts of Wales receive less direct sunlight than the UK average, which reduces annual generation and the overall financial return from solar. This is reflected in your viability score. It does not rule out solar, but it does mean that system sizing, orientation and other factors become more important, and a thorough assessment is essential.';
};

const OCCUPANCY_NARRATIVE = {
  'someone home all day': 'Having someone home all day is one of the strongest positive factors for solar returns. Daytime occupancy means that the electricity your panels generate is used directly in your home as it is produced. Self-consumed solar energy saves you the full retail electricity rate, which is significantly more valuable than exporting surplus to the grid. This pattern of consumption has a strong positive impact on your viability score.',
  'home half the day':    'Being home for half the day provides a reasonable level of daytime self-consumption. During the hours when the property is occupied, generated electricity is used directly, saving you the full retail rate. During unoccupied hours, surplus energy is exported at the lower export rate. This is a common pattern and still supports solid returns, though adding battery storage could further increase the proportion of energy you self-consume.',
  'nobody home during the day': 'With nobody home during the day, most of the electricity your panels generate will be exported to the grid rather than consumed directly in your home. Exported energy earns the SEG rate, which is significantly lower than the retail electricity rate you save when self-consuming. This reduces your year one savings and extends the payback period compared to properties with daytime occupancy. Battery storage could significantly improve your returns by storing generation for use in the evening.',
};
const getOccupancyNarrative = (s) =>
  OCCUPANCY_NARRATIVE[(s || '').toLowerCase()] ?? OCCUPANCY_NARRATIVE['home half the day'];

const getConsumptionNarrative = (kwh) => {
  const k = Number(kwh) || 0;
  if (k < 2000) return 'Your annual electricity consumption is low. A smaller, well-matched solar system can still deliver proportionate savings, but the absolute financial return will be lower than for higher-consuming households. Our experts can help you find the right system size to maximise your return without over-investing in capacity you cannot use.';
  if (k <= 3500) return 'Your annual electricity consumption is broadly in line with the UK average. This is a positive factor for solar viability — a standard residential system can be sized to match your usage closely, helping you maximise self-consumption and achieve a competitive payback period. This range represents the core target profile for residential solar in the UK.';
  if (k <= 5000) return 'Your annual electricity consumption is higher than the UK average. This is good news for solar viability. Higher consumption means more of your generated electricity can be used directly in your home at the full retail rate, improving self-sufficiency and reducing payback period. A larger system is likely to be justified and our experts can help you size it correctly.';
  return 'Your annual electricity consumption is significantly above the UK average. This strongly supports the case for solar. High consumption households benefit the most from solar in absolute terms — more generation is self-consumed at the full retail rate, reducing bills substantially. It may also be worth considering battery storage to maximise returns around the clock.';
};

const ROOF_CONDITION_NARRATIVE = {
  excellent: 'You have indicated that your roof is in excellent condition. A structurally sound, well-maintained roof provides the ideal foundation for a solar installation and means panels can be fitted without any prior remedial work. This reduces both the complexity and the overall cost of your project and is a positive factor in your viability score.',
  good:      'You have indicated that your roof is in good condition. A roof in good condition is suitable for solar installation without significant preparatory work. Minor maintenance may be advisable as part of a long-term plan, but it should not delay or complicate an installation at this stage. This is a positive factor in your viability score.',
  fair:      'You have indicated that your roof is in fair condition. Before committing to a solar installation it would be sensible to have the roof inspected by a qualified roofer. Panels are typically warranted for 25 years and are designed to outlast most roof coverings, so addressing any underlying condition issues before installation avoids the need for costly panel removal and re-installation later. This factor has a modest downward effect on your score.',
  poor:      'You have indicated that your roof is in poor condition. A solar installation on a roof in poor condition carries meaningful risk. Panels are typically warranted for 25 years and removing them to undertake roof repairs after installation is costly and disruptive. We strongly recommend having the roof professionally assessed and any necessary repairs carried out before proceeding with solar. This factor has a meaningful downward effect on your viability score.',
};
const getRoofConditionNarrative = (c) =>
  ROOF_CONDITION_NARRATIVE[(c || '').toLowerCase()] ?? ROOF_CONDITION_NARRATIVE.good;

const EV_NARRATIVE = {
  'no':                       'You do not currently charge an electric vehicle at your property. Solar and EV ownership are a powerful combination — charging a vehicle from your own generated electricity is one of the most effective ways to increase self-consumption and maximise the value of a solar installation. If you are considering an EV in the future, solar is well worth factoring into your plans.',
  'yes: 1 vehicle':           'You charge one electric vehicle at your property. This is one of the most effective ways to increase self-consumption from a solar installation. Charging during daylight hours — even partially — means a significant portion of your generated electricity is used at the full retail rate rather than exported at the lower SEG rate. An EV combined with solar can substantially improve your payback period and overall returns.',
  'yes: more than 1 vehicle': 'You charge more than one electric vehicle at your property. Multiple EVs dramatically increase your daytime electricity demand and create an excellent opportunity to self-consume a large proportion of your solar generation. Charging from your own solar electricity rather than the grid maximises the value of every unit generated. This is one of the strongest use cases for a residential solar installation and has a meaningful positive impact on your viability score.',
};
const getEVNarrative = (ev) =>
  EV_NARRATIVE[(ev || '').toLowerCase()] ?? EV_NARRATIVE['no'];

export default function OutputScreen({ nextStep, formData }) {
  
  const {
    roofArea,
    orientation,
    shadingPercent,
    shadingTime,
    electricityConsumption,
    occupancyFactor,
    postcode,
    houseType,
    occupancySchedule,
    roofCondition,
    evOwnership,
  } = formData;

  const systemSizeKwp = +(roofArea * 0.225).toFixed(2);
  const normalizedOrientation = normalizeOrientation(orientation);
  const occupancyDecimal = occupancyFactor > 1 ? occupancyFactor / 100 : occupancyFactor;
  const ELECTRICITY_RATE = 0.2467;

  const electricityConsumptionKwh = electricityConsumption;

  const annualGeneration = calculateAnnualGeneration(
    systemSizeKwp,
    normalizedOrientation,
    shadingPercent,
    shadingTime
  );

  const { selfConsumed, exported } = calculateConsumptionExport(
    annualGeneration,
    electricityConsumptionKwh,
    occupancyDecimal
  );

  const year1Savings = calculateYear1Savings(selfConsumed, exported);
  const lifetimeSavings = calculateLifetimeSavings(selfConsumed, exported);
  const installationCost = calculateInstallationCost(systemSizeKwp);
  const paybackPeriod = calculatePaybackPeriod({ year1Savings, installationCost });
  const viability = calculateViabilityScore(formData, { paybackPeriod });
  const normalizedIrradiance = getNormalizedIrradiance(postcode);
  const narrativeCards = [
    { factor: 'Orientation',       text: getOrientationNarrative(orientation) },
    { factor: 'Shading',           text: getShadingNarrative(shadingPercent, shadingTime) },
    { factor: 'Location',          text: getLocationNarrative(normalizedIrradiance) },
    { factor: 'Occupancy',         text: getOccupancyNarrative(occupancySchedule) },
    { factor: 'Electricity Usage', text: getConsumptionNarrative(electricityConsumption) },
    { factor: 'Roof Condition',    text: getRoofConditionNarrative(roofCondition) },
    { factor: 'EV Ownership',      text: getEVNarrative(evOwnership) },
  ];

  const EXPORT_RATE = 0.12;
  const CO2_SAVINGS_PER_KWH = 0.233; // UK average CO2 in kg/kWh

  const annualGenerationSavings = selfConsumed * ELECTRICITY_RATE;
  const exportIncome = exported * EXPORT_RATE;
  const energyBillSavings = annualGenerationSavings;
  const monthlyBillReduction = (selfConsumed / 12) * ELECTRICITY_RATE;
  const co2Savings = annualGeneration * CO2_SAVINGS_PER_KWH;

  const selfSufficiency = electricityConsumptionKwh
    ? ((selfConsumed / electricityConsumptionKwh) * 100).toFixed(0)
    : 0;

  const CASHFLOW_YEARS = 25;
const annualSavingsYear1 = year1Savings;
const inflationRate = 0.02;

let cumulativeSavings = 0;
const cashflowData = [];

for (let year = 1; year <= CASHFLOW_YEARS; year++) {
  const adjustedAnnualSavings =
    annualSavingsYear1 * Math.pow(1 + inflationRate, year - 1);
  cumulativeSavings += adjustedAnnualSavings;

  cashflowData.push({
    year: `Year ${year}`,
    annualSavings: adjustedAnnualSavings,
    cumulativeSavings: Math.round(cumulativeSavings),
    installationCost,
    isPaybackYear:
      cumulativeSavings >= installationCost &&
      (cumulativeSavings - adjustedAnnualSavings) < installationCost,
  });
}

const paybackYear = cashflowData.find((d) => d.isPaybackYear)?.year || "N/A";

const formatCurrency = (value, isMobile) => {
  if (isMobile && value >= 1000) {
    return `£${(value / 1000).toFixed(0)}k`;
  }
  return `£${value.toLocaleString()}`;
};

const numPeople = formData.numPeople || "N/A";
const occupancy = formData.occupancySchedule || "No occupancy data";

// Compose display string
const electricityConsumptionPattern = `${numPeople} ${numPeople > 1 ? "people" : "person"}, ${occupancy}`;

const [batteryCTAClicked, setBatteryCTAClicked] = useState("Not Clicked");
const [speakCTAClicked, setSpeakCTAClicked] = useState("Not Clicked");
const submitSentRef = useRef(false);
const ctaEmailSentRef = useRef(false);

const calculatedResults = {
  systemSizeKwp,
  panelCount: Math.round(systemSizeKwp / 0.45),
  annualGeneration: Math.round(annualGeneration),
  selfConsumed: Math.round(selfConsumed),
  exported: Math.round(exported),
  year1Savings: Math.round(year1Savings),
  annualBillSaving: Math.round(energyBillSavings),
  annualExportIncome: Math.round(exportIncome),
  totalAnnualBenefit: Math.round(year1Savings),
  installationCost: Math.round(installationCost),
  paybackPeriod,
  lifetimeSavings: Math.round(lifetimeSavings),
  selfSufficiency,
  monthlyBillReduction: Math.round(monthlyBillReduction),
  co2Savings: Math.round(co2Savings),
  viabilityScore: viability.totalScore,
  viabilityBand: viability.band,
};

useEffect(() => {
  if (!submitSentRef.current) {
    submitSentRef.current = true;
    fetch('/api/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ formData, calculatedResults }),
    }).catch(err => console.error('Submit error:', err));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  if (
    submitSentRef.current &&
    !ctaEmailSentRef.current &&
    (batteryCTAClicked === "Clicked" || speakCTAClicked === "Clicked")
  ) {
    ctaEmailSentRef.current = true;
    const ctaType = batteryCTAClicked === "Clicked" ? "Discuss battery storage" : "Speak to us";
    fetch('/api/cta-clicked', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ formData, calculatedResults, ctaType }),
    }).catch(err => console.error('CTA error:', err));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [batteryCTAClicked, speakCTAClicked]);

const [showCTAMessage, setShowCTAMessage] = useState(false);
const [showToast, setShowToast] = useState(false);

useEffect(() => {
  const timer = setTimeout(() => setShowToast(true), 4000);
  return () => clearTimeout(timer);
}, []);

const handleBatteryClick = () => {
  setBatteryCTAClicked("Clicked");
  setShowCTAMessage(true);
};

const handleSpeakClick = () => {
  setSpeakCTAClicked("Clicked");
  setShowCTAMessage(true);
};

const isMobile = window.innerWidth < 768;

  const BAND_DESCRIPTIONS = {
    'Excellent':
      'A score in this range puts your property among the best candidates for solar in the UK. You can expect strong generation performance, a competitive payback period and substantial savings over the lifetime of the system. The financial case for solar at your property is compelling and an installation is very likely to meet or exceed typical performance benchmarks. We would encourage you to move forward with confidence and get a full site assessment to confirm system sizing and finalise your numbers.',
    'Good':
      'A score in this range means solar is a sound investment for your property. You can expect solid generation performance and meaningful bill savings from day one. Your payback period should fall comfortably within the system lifetime, leaving many years of net profit. The conditions at your property are favourable and there is no significant reason to hesitate. A full site assessment will give you precise figures and confirm the right system size for your needs.',
    'Moderate':
      'A score in this range means solar has genuine potential at your property but the returns may be more variable than for higher scoring properties. Some factors are working in your favour while others are creating drag on performance or payback. The system could still deliver meaningful savings over its lifetime but the financial case requires closer examination. We recommend a full independent assessment to understand exactly how the limiting factors affect your specific situation before you commit.',
    'Below average':
      'A score in this range indicates that one or more significant factors are constraining solar performance at your property. While an installation may still be viable, the returns are likely to be more modest and the payback period longer than average. It would not be responsible to proceed without a thorough independent site survey. A qualified assessor will be able to identify whether there are practical measures — improved panel positioning, battery storage or grid optimisation — that could improve the outcome for your property.',
    'Poor':
      'A score in this range suggests that solar faces material challenges at your property in its current form. The combination of factors assessed points to meaningful constraints on generation, savings or both. This does not necessarily mean solar is impossible, but it does mean that a standard installation is unlikely to deliver strong returns without further investigation. An independent site survey is essential before any decisions are made. There may be bespoke solutions available, but these need to be assessed properly by a qualified professional with eyes on your property.',
  };

  const bandSlug = {
    'Excellent': 'excellent', 'Good': 'good', 'Moderate': 'moderate',
    'Below average': 'below-average', 'Poor': 'poor',
  }[viability.band] ?? 'moderate';

  return (
    <>
    {showToast && (
      <div className="toast-notification">
        <div className="toast-content">
          <p className="toast-text">
            We've sent your Solar Viability Report to your email address. Unlike advice from an installer, everything in it is written with your interests — not ours — in mind. We hope it gives you the clarity and confidence to make the right decision for your home.
          </p>
          <button className="toast-close" onClick={() => setShowToast(false)} aria-label="Close notification">✕</button>
        </div>
      </div>
    )}
    <div className="output-container">
      {/* New Styled Header and Key Metrics */}
      <div className="dashboard-header-card">
        <h1 className="dashboard-heading">Your personalised solar potential dashboard</h1>

        <div className="dashboard-boxes">
          {/* Installation Cost */}
          <div className="dashboard-box">
            <div className="dashboard-icon-circle">
              <i className="fas fa-pound-sign dashboard-icon"></i>
            </div>
            <div className="dashboard-info">
              <div className="dashboard-value">£{installationCost.toLocaleString()}</div>
              <div className="dashboard-label">Estimated installation cost</div>
            </div>
          </div>

          {/* Year 1 Savings */}
          <div className="dashboard-box">
            <div className="dashboard-icon-circle">
              <i className="fas fa-pound-sign dashboard-icon"></i>
            </div>
            <div className="dashboard-info">
              <div className="dashboard-value">£{year1Savings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
              <div className="dashboard-label">Estimated year 1 savings inc export payments</div>
            </div>
          </div>

          {/* Self-Sufficiency */}
          <div className="dashboard-box">
            <div className="dashboard-icon-circle icon-percent">
              <i className="fas fa-percent dashboard-icon"></i>
            </div>
            <div className="dashboard-info">
              <div className="dashboard-value">{selfSufficiency}%</div>
              <div className="dashboard-label">Electricity self-sufficiency</div>
            </div>
          </div>
        </div>
      </div>

      {/* Viability Score */}
      <div className="viability-score-section">
        <h2 className="viability-heading">Solar Viability Score</h2>

        <div className="viability-score-display">
          <span className="viability-number">{viability.totalScore}</span>
          <span className="viability-denom">/ 100</span>
          <span className={`viability-band-label viability-band-label--${bandSlug}`}>
            {viability.band}
          </span>
        </div>

        <div className="viability-bar-track">
          <div
            className={`viability-bar-fill viability-bar-fill--${bandSlug}`}
            style={{ '--viability-score': viability.totalScore }}
          />
        </div>

        <p className="viability-description">{BAND_DESCRIPTIONS[viability.band]}</p>
      </div>

      <div className="viability-narrative-wrapper">
        <div className="viability-narrative-section">
          <h3 className="viability-narrative-heading">What this means for your property</h3>
          <div className="viability-cards-grid">
            {narrativeCards.map(({ factor, text }) => (
              <div key={factor} className="viability-card">
                <h4 className="viability-card-heading">{factor}</h4>
                <p className="viability-card-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* New Property Details Section */}
      <div className="property-details-section white-bg">
        <div className="property-details-container">
          <div className="property-image">
            <HouseImage/>
          </div>
          <div className="property-text">
            <div>
              <strong>Your property:</strong><br />
              {houseType} in {postcode}.
            </div>

            <br />

            <div>
              <strong>Solar panels:</strong><br />
              We estimate your property could have <strong>{Math.round(systemSizeKwp / 0.45)}</strong> panels with a 450w rating.
            </div>

            <br />

            <div>
              <strong>Roof:</strong><br />
              Based on the inputs gathered, your useable roof area for solar is assumed to be {roofArea} m².
            </div>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="system-summary-section">
        <h2 className="system-summary-heading">Summary of your system and how it could perform:</h2>
        <div className="system-summary-grid">
          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-solar-panel teal-icon"></i></div>
            <div className="metric-label">System size</div>
            <div className="metric-value">{systemSizeKwp.toFixed(1)} kWp</div>
          </div>

          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-bolt teal-icon"></i></div>
            <div className="metric-label">Annual generation</div>
            <div className="metric-value">{annualGeneration.toLocaleString()} kWh</div>
          </div>


          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-hourglass-half teal-icon"></i></div>
            <div className="metric-label">Payback period</div>
            <div className="metric-value">{paybackPeriod} years</div>
          </div>

          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-piggy-bank teal-icon"></i></div>
            <div className="metric-label">Lifetime savings</div>
            <div className="metric-value">£{Math.round(lifetimeSavings).toLocaleString()}</div>
          </div>

          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-wallet teal-icon"></i></div>
            <div className="metric-label">Energy bill savings per year</div>
            <div className="metric-value">£{energyBillSavings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
          </div>

          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-share teal-icon"></i></div>
            <div className="metric-label">Export income per year</div>
            <div className="metric-value">£{exportIncome.toFixed(0)}</div>
          </div>

          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-calculator teal-icon"></i></div>
            <div className="metric-label">Estimated monthly bill reduction</div>
            <div className="metric-value">£{monthlyBillReduction.toFixed(0)}</div>
          </div>

          <div className="metric-item">
            <div className="icon-circle"><i className="fas fa-leaf teal-icon"></i></div>
            <div className="metric-label">CO₂ you will save per year</div>
            <div className="metric-value">{co2Savings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kg</div>


          </div>
        </div>
      </div>

<h2 className="cashflow-heading">
  Cashflow projection: estimated lifetime savings and payback period modeling
</h2>

<div className="cashflow-chart-wrapper">
  <ResponsiveContainer width="100%" height={300}>
    <BarChart data={cashflowData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
      <XAxis 
  dataKey="year"
  label={{
    value: 'Year',
    position: 'insideBottom',
    offset: -5,
    dy: 20,
    className: 'axis-label'
  }}
/>

<YAxis 
  tickFormatter={(value) => formatCurrency(value, isMobile)}
  label={{
    value: 'Cumulative Net Savings (£)',
    angle: -90,
    position: 'insideLeft',
    offset: 10,
    dx: isMobile ? 0 : -25, // adjust spacing left/right
    dy: 0,
    style: { textAnchor: "middle", fontSize: isMobile ? 10 : 14 },
    className: 'axis-label'
  }}
/>
      <Tooltip
        formatter={(value) => [`£${value.toLocaleString()}`, "Cumulative Savings"]}
      />
      <Bar dataKey="cumulativeSavings" fill="#14B8A6" />
      <ReferenceLine
    y={installationCost}
    stroke="gray"
    strokeDasharray="4 4"
    label={{ value: "Installation Cost", position: "insideBottomLeft", fill: "gray", className: "install-cost-label" }}
  />
      
      <ReferenceLine
        x={paybackYear}
        stroke="red"
        
        label={{ value: "Payback Year", position: "insideTopLeft", fill: "red", className: "payback-label" }}
      />
    </BarChart>
  </ResponsiveContainer>
</div>

<div className="assumptions-section">
  <h2 className="assumptions-heading">Assumptions and information your estimate is based on:</h2>
  <div className="assumptions-grid">

    <div className="assumption-item">
      <div className="icon-circle grey-bg"><i className="fas fa-bolt"></i></div>
      <div className="assumption-label">Annual Consumption</div>
      <div className="assumption-value">{electricityConsumptionKwh} kWh</div>
    </div>

    <div className="assumption-item">
      <div className="icon-circle grey-bg"><i className="fas fa-compass"></i></div>
      <div className="assumption-label">Orientation</div>
      <div className="assumption-value">{orientation}</div>
    </div>

    <div className="assumption-item">
      <div className="icon-circle grey-bg"><i className="fas fa-cloud-sun"></i></div>
      <div className="assumption-label">Shading</div>
      <div className="assumption-value">{shadingPercent}% : {shadingTime}</div>
    </div>

    <div className="assumption-item">
      <div className="icon-circle grey-bg"><i className="fas fa-clock"></i></div>
      <div className="assumption-label">Consumption Pattern</div>
      <div className="assumption-value">{electricityConsumptionPattern}</div>
    </div>

    <div className="assumption-item">
      <div className="icon-circle grey-bg"><i className="fas fa-pound-sign"></i></div>
      <div className="assumption-label">Electricity Price</div>
      <div className="assumption-value">£{ELECTRICITY_RATE.toFixed(4)} per kWh</div>
    </div>

    <div className="assumption-item">
      <div className="icon-circle grey-bg"><i className="fas fa-sign-out-alt"></i></div>
      <div className="assumption-label">Grid Export Rate</div>
      <div className="assumption-value">£{EXPORT_RATE.toFixed(2)} per kWh</div>
    </div>

  </div>
</div>

<div className="recommendations-container">
  <div className="smart-recommendations">
    <h2>Smart recommendations for you based on your project parameters</h2>

  {formData.shadingPercent > 34 && formData.shadingTime === "Afternoon" && (
    <div className="recommendation-block">
      <h3>Shading</h3>
      <p>
        Due to high afternoon shading, panels installed on an east-facing roof may provide better performance. Get in touch so we can do a full, no obligation analysis for you to optimise the benefits you could achieve from solar.
      </p>
      <div className="property-image">
            <QuietStreet />
        </div>
    </div>
  )}

    {formData.shadingPercent > 34 && formData.shadingTime === "Morning" && (
      <div className="recommendation-block">
        <h3>Shading</h3>
        <p>
          Morning shading detected. West-facing panels could optimise your energy generation later in the day. Get in touch so we can do a full, no obligation analysis for you to optimise the benefits you could achieve from solar.
        </p>
        <div className="property-image">
            <QuietStreet />
        </div>
      </div>
    )}

    {formData.shadingPercent > 34 && formData.shadingTime === "all-day" && (
      <div className="recommendation-block">
        <h3>Shading</h3>
        <p>
          Significant shading is present throughout the day. Ask us to do a full shading analysis to help determine viability. We’ll help you understand your options and if anything can be done to reduce shading and help you save more from solar over the long term.
        </p>
        <div className="property-image">
            <QuietStreet />
        </div>
      </div>
    )}

    {formData.shadingPercent < 1 && (
      <div className="recommendation-block">
        <h3>Shading</h3>
        <p>
          You indicated your roof is not affected by shading which is the perfect scenario for anyone considering solar for their home. An un-shaded roof will generate the maximum amount of free energy to contribute towards your overall returns.
        </p>
        <div className="property-image">
            <QuietStreet />
        </div>
      </div>
    )}

    {formData.occupancySchedule === "nobody home during the day" && (
      <div className="recommendation-block">
        <h3>Occupancy and battery storage</h3>
        <p>
          With no one home during the day, adding a battery could help you store solar energy for evening use and increase your return on investment further.
        </p>
        <div className="property-image">
            <SeasonChangeImage/>
        </div>
      </div>
    )}

    {formData.occupancySchedule === "home half the day" && (
      <div className="recommendation-block">
        <h3>Occupancy and battery storage</h3>
        <p>
          As no-one is home for parts of the day, adding a battery could help you store solar energy for evening use when you are home and increase your return on investment further.
        </p>
        <div className="property-image">
            <SeasonChangeImage/>
        </div>
      </div>
    )}

    {formData.occupancySchedule === "someone home all day" && (
      <div className="recommendation-block">
        <h3>Occupancy and battery storage</h3>
        <p>
          You save more consuming your solar energy than what you make exporting it to the grid so daytime occupancy enhances your returns. Even when home all day, there can be an excess of energy produced so pairing with a battery can enhance returns.
        </p>
        <div className="property-image">
            <SeasonChangeImage/>
        </div>
      </div>
    )}

    <div className="recommendation-block">
      <h3>Electricity tariff</h3>
      <p>
        To compliment a solar PV array, consider exploring time-of-use tariffs to make the most of cheaper night rates when your solar system is not generating.
      </p>
      <div className="property-image">
            <DataTrendsImage/>
        </div>
    </div>

    {formData.electricityConsumption < 2866 && (
      <div className="recommendation-block">
        <h3>System size fine-tuning</h3>
        <p>
          Your annual electricity consumption is lower than the UK average meaning you don’t need to invest in a big solar PV system to get expected returns. Our experts will help you find the perfect balance between upfront costs and bill savings.
        </p>
        <div className="property-image">
            <FineTuneImage/>
        </div>
      </div>
    )}

    {formData.electricityConsumption > 2865 && (
      <div className="recommendation-block">
        <h3>System size fine-tuning</h3>
        <p>
          Your annual usage is higher than the UK average, suggesting a larger solar PV system would maximise your savings. Talk to our experts to work out the best approach and find the perfect balance between upfront cost and bill savings.
        </p>
        <div className="property-image">
            <FineTuneImage/>
        </div>
      </div>
    )}
  </div>
  </div>

<div className="battery-section">
  <h2 className="battery-heading">Battery storage potential</h2>
  <div className="battery-content">
    <div className="battery-icon-container">
      <i className="fas fa-battery-three-quarters fa-rotate-270 battery-icon"></i>
    </div>
    <div className="battery-text">
      <p>
        Many homeowners wonder if a battery is worth it. Adding battery storage can be the perfect
        partner for a solar PV system, enabling you to use even more of the electricity generated on
        your roof. We’ve not included a battery here but if you’d like to find out more, click below
        to arrange for one of our experts to call you.
      </p>
    </div>
    <div className="battery-icon-container right-icon">
      <i className="fas fa-battery-three-quarters fa-rotate-270 battery-icon"></i>
    </div>
  </div>
   <button className="cta-button" onClick={handleBatteryClick}>Discuss battery storage</button>
</div>

{/* co2Savings already in kg CO₂ per year.
Using UK government reference values:
Petrol car ~0.24 kg CO₂ per mile (DEFRA/BEIS data)
Tree absorbs ~21 kg CO₂ per year (Woodland Trust UK approximation)
EV ~0.0466 kg CO₂ per mile (UK grid average) */}

<div className="environmental-impact-section">
  <h2>Equivalent Environmental Impact</h2>
  <div className="impact-cards">

    <div className="impact-card">
      <h3>Driving Miles Saved</h3>
      <p>Your solar system’s carbon savings are equivalent to:</p>
      <p className="impact-number">
        {(co2Savings / 0.24).toLocaleString(undefined, { maximumFractionDigits: 0 })} miles not driven
      </p>
      <div className="impact-icons">
        {Array(Math.min(Math.floor(co2Savings / 100), 10)).fill().map((_, i) => (
          <span key={i} role="img" aria-label="car">🚗</span>
        ))}
      </div>
    </div>

    <div className="impact-card">
      <h3>CO₂ Absorbed by Trees</h3>
      <p>This is like:</p>
      <p className="impact-number">
        {Math.round(co2Savings / 21)} trees absorbing CO₂ for a year
      </p>
      <div className="impact-icons">
        {Array(Math.min(Math.round(co2Savings / 100), 10)).fill().map((_, i) => (
          <span key={i} role="img" aria-label="tree">🌳</span>
        ))}
      </div>
    </div>

    <div className="impact-card">
      <h3>Electric Miles Charged</h3>
      <p>Your solar saving equals:</p>
      <p className="impact-number">
        {Math.round(co2Savings / 0.0466).toLocaleString()} Electric vehicle miles charged
      </p>
      <div className="impact-icons">
        {Array(Math.min(Math.floor(co2Savings / 100), 10)).fill().map((_, i) => (
          <span key={i} role="img" aria-label="electric car">🔌</span>
        ))}
      </div>
    </div>

  </div>
</div>

<div className="next-steps-section">
  <h2>Your next steps</h2>
  <div className="next-steps-content">
    <img src={ContactUsImage} alt="Contact us" className="next-steps-image" />
    <div className="next-steps-text">
      <p>
        We can help you decide with complete confidence whether solar is right for you and your home and help you achieve the very best possible return on investment by ensuring your system is designed with you and your family in mind. We make sure it satisfies your objectives and not those of an installation contractor.
      </p>
      <p>
        Armed with independent, honest numbers, you're in control. If solar makes sense for your home, we'll connect you with vetted, trusted and quality-assured installers. If it doesn't make sense for you to proceed, we'll tell you that too. You won't get that level of transparency if you go direct to an installer.
      </p>
      <p>
        WHY TRUST OPTIFY:
        The solar market has a problem. Most advice comes from people who profit from selling you a system regardless of whether it's right for your home. Optify is different. We're independent advisors with no installation business and no financial interest in which installer you choose. We only recommend solar when the numbers genuinely stack up. 
        Our only job is to give you honest, accurate information so you can make the best decision for your home and your finances.
      </p>
        </div>
  </div>
</div>


      <div className="btn-container">
        <button className="next-button" onClick={handleSpeakClick}>Speak to us</button>
      </div>
     {showCTAMessage && (
      <div className="popup-overlay">
        <div className="popup-content">
          <p>Thank you for your request, a member of our team will be in touch shortly.</p>
          <button onClick={() => setShowCTAMessage(false)} className="popup-close-btn">Close</button>
        </div>
      </div>
    )}
  </div>
  </>
);
}
